import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import * as RecordRTC from 'recordrtc';

@Component({
  selector: 'app-recording-audio',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="recorder-container" [class.recording]="isRecording">
      <canvas #audioCanvas [width]="setCostumWidth || setCustomWidth" height="28"></canvas>
      <div class="recording-info" *ngIf="isRecording">
        <span class="recording-dot"></span>
        <span class="recording-time">{{ recordingTime | date:'mm:ss' }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      .recorder-container {
        position: relative;
        border-radius: 8px;
        overflow: hidden;
        margin: 4px 0;
        background-color: #f0f4f8;
        transition: all 0.2s ease;
      }

      .recorder-container.recording {
        box-shadow: 0 0 0 1px rgba(255, 76, 76, 0.3);
        background-color: rgba(255, 76, 76, 0.05);
      }

      canvas {
        display: block;
        width: 100%;
        background: transparent;
        border: none;
      }

      .recording-info {
        position: absolute;
        top: 6px;
        right: 8px;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 2px 8px;
        border-radius: 12px;
        background-color: rgba(255, 255, 255, 0.85);
        backdrop-filter: blur(4px);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .recording-dot {
        display: inline-block;
        width: 6px;
        height: 6px;
        background-color: #ff4c4c;
        border-radius: 50%;
        animation: pulse 1.2s ease-in-out infinite;
      }

      .recording-time {
        font-size: 11px;
        font-weight: 500;
        color: #333;
      }

      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.4); opacity: 0.7; }
        100% { transform: scale(1); opacity: 1; }
      }
    `,
  ],
})
export class RecordingAudioComponent implements OnDestroy {
  @Output() recordingComplete = new EventEmitter<File>();
  @ViewChild('audioCanvas', { static: false })
  audioCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() set currentAction(currentAction: any) {
    this.actionTrigger(currentAction);
  }
  @Input() setCostumWidth: number = 500;
  @Input() setCustomWidth: number = 500;

  isRecording = false;
  recorder: any;
  stream: MediaStream | undefined;
  animationFrameId: number | undefined;
  recordingTime: Date = new Date(0);
  timerId: any;

  ngOnDestroy() {
    this.stopRecording();
    this.stopVisualization();
    this.stopTimer();
  }

  actionTrigger(value: any) {
    if (value) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  startRecording() {
    this.isRecording = true;
    this.resetTimer();
    this.startTimer();

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.stream = stream;
        this.recorder = new RecordRTC.StereoAudioRecorder(stream, {
          type: 'audio',
          mimeType: 'audio/webm',
        });

        this.recorder.record();
        this.visualizeAudio(stream);
      })
      .catch((error) => {
        console.error('Error accessing microphone:', error);
        this.isRecording = false;
        this.stopTimer();
      });
  }

  stopRecording() {
    if (this.recorder && this.isRecording) {
      this.isRecording = false;
      this.stopTimer();
      
      this.recorder.stop((blob: Blob) => {
        this.convertToMP3(blob);
        this.stopVisualization();
      });

      this.stream?.getTracks().forEach((track) => track.stop());
    }
  }

  convertToMP3(blob: Blob) {
    const audioFile = new File([blob], `recording-${Date.now()}.mp3`, {
      type: 'audio/mpeg',
    });

    this.recordingComplete.emit(audioFile);
    this.resetCanvas();
  }

  visualizeAudio(stream: MediaStream) {
    const canvas = this.audioCanvas.nativeElement;
    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) return;

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 512;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);

    const draw = () => {
      if (!this.isRecording) return;
      this.animationFrameId = requestAnimationFrame(draw);
      
      analyser.getByteFrequencyData(dataArray);
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      
      const centerY = canvas.height / 2;
      const barWidth = canvas.width / (bufferLength / 4);
      
      // Draw center line
      canvasCtx.beginPath();
      canvasCtx.moveTo(0, centerY);
      canvasCtx.lineTo(canvas.width, centerY);
      canvasCtx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
      canvasCtx.stroke();
      
      // Modern waveform visualization
      canvasCtx.beginPath();
      canvasCtx.moveTo(0, centerY);
      
      for (let i = 0; i < bufferLength; i += 4) {
        const height = (dataArray[i] / 255.0) * (canvas.height / 2);
        const x = (i / 4) * barWidth;
        
        // Create smooth curve instead of bars
        if (i === 0) {
          canvasCtx.moveTo(x, centerY + height);
        } else {
          canvasCtx.lineTo(x, centerY + height);
        }
      }
      
      for (let i = bufferLength - 4; i >= 0; i -= 4) {
        const height = (dataArray[i] / 255.0) * (canvas.height / 2);
        const x = (i / 4) * barWidth;
        canvasCtx.lineTo(x, centerY - height);
      }
      
      canvasCtx.closePath();
      
      // Create gradient
      const gradient = canvasCtx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, 'rgba(76, 132, 255, 0.6)');
      gradient.addColorStop(0.5, 'rgba(65, 193, 255, 0.6)');
      gradient.addColorStop(1, 'rgba(76, 132, 255, 0.6)');
      
      canvasCtx.fillStyle = gradient;
      canvasCtx.fill();
    };

    draw();
  }

  startTimer() {
    this.resetTimer();
    this.timerId = setInterval(() => {
      this.recordingTime = new Date(this.recordingTime.getTime() + 1000);
    }, 1000);
  }

  stopTimer() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  resetTimer() {
    this.stopTimer();
    this.recordingTime = new Date(0);
  }

  stopVisualization() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
  }

  resetCanvas() {
    const canvas = this.audioCanvas.nativeElement;
    const canvasCtx = canvas.getContext('2d');
    if (canvasCtx) {
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw idle state
      const centerY = canvas.height / 2;
      
      // Draw center line
      canvasCtx.beginPath();
      canvasCtx.moveTo(0, centerY);
      canvasCtx.lineTo(canvas.width, centerY);
      canvasCtx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
      canvasCtx.stroke();
      
      // Draw subtle idle waveform
      canvasCtx.beginPath();
      for (let x = 0; x < canvas.width; x += 3) {
        const y = centerY + Math.sin(x * 0.05) * 2;
        if (x === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }
      }
      canvasCtx.strokeStyle = 'rgba(0, 0, 0, 0.07)';
      canvasCtx.stroke();
    }
  }
}